import { useNostr } from '@nostrify/react';
import { useMutation } from '@tanstack/react-query';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';

export interface FollowResult {
  alreadyFollowing: boolean;
}

/**
 * Follow a pubkey by merging them into the logged-in user's kind-3
 * contact list. The existing list is always preserved — we only append.
 */
export function useFollow() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();

  return useMutation<FollowResult, Error, string>({
    mutationFn: async (targetPubkey) => {
      if (!user) throw new Error('Not logged in');

      const [existing] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.timeout(6000) },
      );

      if (existing?.tags.some(([name, value]) => name === 'p' && value === targetPubkey)) {
        return { alreadyFollowing: true };
      }

      await publish({
        kind: 3,
        content: existing?.content ?? '',
        tags: [...(existing?.tags ?? []), ['p', targetPubkey]],
      });

      return { alreadyFollowing: false };
    },
  });
}
